import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  CircularProgress,
  FormControl,
  GKModal,
  GKSearchSelect,
  GKTextField,
  Grid2,
} from "components";
import { useFormik } from "formik";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";

interface LocationOption {
  id: number;
  name: string;
}

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  data?: any;
}

type LocationKey = "country" | "state" | "city";

const DetailsModal = (props: Props) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { open, setOpen, data } = props;

  const { mutate, isLoading } = useMutation({
    mutationKey: ["UPDATE_ADMIN_DETAILS"],
    mutationFn: (values: any) =>
      axiosInstance.patch(`/super_admin/${id}/advisor-details/`, values),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["GET_ADMIN_DETAILS"] });
      setOpen(false);
      toast.success(response?.data?.message || "Update Successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const formik = useFormik({
    initialValues: {
      sebiNumber: data?.sebiNumber || "",
      gst: data?.gst || "",
      address: data?.address || "",
      pincode: data?.pincode || "",
      country: null as LocationOption | null,
      state: null as LocationOption | null,
      city: null as LocationOption | null,
    },
    onSubmit: (values) =>
      mutate({
        address: values.address,
        pincode: values.pincode,
        country: values.country?.name || "",
        state: values.state?.name || "",
        city: values.city?.name || "",
      }),
  });

  const { data: countryData } = useQuery({
    queryKey: ["GET_ALL_COUNTRY"],
    queryFn: () => axiosInstance.get(`/country/`),
  });

  const { data: stateData } = useQuery({
    queryKey: ["GET_STATE_BY_COUNTRY", formik.values.country?.id],
    queryFn: () =>
      axiosInstance.get(`/state/?country_id=${formik.values.country?.id}`),
    enabled: !!formik.values.country?.id,
  });

  const { data: cityData } = useQuery({
    queryKey: ["GET_STATE_BY_CITY", formik.values.state?.id],
    queryFn: () =>
      axiosInstance.get(`/city/?state_id=${formik.values.state?.id}`),
    enabled: !!formik.values.state?.id,
  });

  // Prefill country/state/city by matching the saved names with the option lists
  useEffect(() => {
    const options = { country: countryData, state: stateData, city: cityData };

    (["country", "state", "city"] as LocationKey[]).forEach((key) => {
      if (formik.values[key]?.id || !data?.[key]) return;
      const match = options[key]?.data?.data?.find(
        (item: LocationOption) =>
          item?.name?.toLowerCase() === String(data[key]).toLowerCase()
      );
      if (match) formik.setFieldValue(key, match);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryData, stateData, cityData, data]);

  const renderLocationSelect = (
    key: LocationKey,
    label: string,
    options: LocationOption[],
    onSelect: (val: LocationOption | null) => void,
    disabled = false
  ) => (
    <GKSearchSelect
      inputLabel={label}
      options={options}
      disabled={disabled}
      value={formik.values[key]}
      onChange={(_: any, val: LocationOption | null) => onSelect(val)}
      getOptionLabel={(opt: LocationOption) => opt?.name || ""}
      isOptionEqualToValue={(opt: LocationOption, val: LocationOption) =>
        opt?.id === val?.id
      }
      renderInput={(params: any) => (
        <GKTextField
          {...params}
          variant="outlined"
          placeholder={`Select ${label}`}
          fullWidth
        />
      )}
    />
  );

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle={"Edit Details"}
      width={"600px"}
      footer={
        <Grid2
          container
          size={12}
          justifyContent={"flex-end"}
          alignContent={"center"}
          gap={1}
        >
          <Button variant="lightBg" onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </Grid2>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="SEBI Number"
                name="sebiNumber"
                value={formik.values.sebiNumber}
                disabled
              />
            </FormControl>
          </Grid2>
          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="GST"
                name="gst"
                value={formik.values.gst}
                disabled
              />
            </FormControl>
          </Grid2>
          <Grid2 size={12}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Address"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
              />
            </FormControl>
          </Grid2>
          <Grid2 size={6}>
            {renderLocationSelect(
              "country",
              "Country",
              countryData?.data?.data || [],
              (val) => {
                formik.setFieldValue("country", val);
                formik.setFieldValue("state", null);
                formik.setFieldValue("city", null);
              }
            )}
          </Grid2>
          <Grid2 size={6}>
            {renderLocationSelect(
              "state",
              "State",
              stateData?.data?.data || [],
              (val) => {
                formik.setFieldValue("state", val);
                formik.setFieldValue("city", null);
              },
              !formik.values.country?.id
            )}
          </Grid2>
          <Grid2 size={6}>
            {renderLocationSelect(
              "city",
              "City",
              cityData?.data?.data || [],
              (val) => formik.setFieldValue("city", val),
              !formik.values.state?.id
            )}
          </Grid2>
          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKTextField
                inputLabel="Pincode"
                name="pincode"
                value={formik.values.pincode}
                onChange={formik.handleChange}
              />
            </FormControl>
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default DetailsModal;
